import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { UserRef } from "@/lib/types"

// Map entity types to API resource paths
const RESOURCE_PATHS: Record<string, string> = {
  Company: "companies",
  Contact: "contacts",
  Deal: "deals",
  ServiceTicket: "service_tickets",
}

export interface RelatedContact {
  id: string
  first_name: string
  last_name: string
  full_name: string
  email: string | null
  phone: string | null
}

export interface RelatedDeal {
  id: string
  name: string
  status: string
  value: number | null
  currency: string
  phase: { id: string; name: string; color: string } | null
  owner: UserRef | null
  created_at: string
}

export interface RelatedTask {
  id: string
  title: string
  status: string
  priority: string
  due_date: string | null
  assignee: UserRef | null
  created_at: string
}

export interface RelatedServiceTicket {
  id: string
  title: string
  status: string
  priority: string
  phase: { id: string; name: string; color: string } | null
  assigned_to: UserRef | null
  created_at: string
}

export interface RelatedDocument {
  id: string
  name: string
  content_type: string | null
  file_size: number | null
  uploaded_by: UserRef | null
  created_at: string
}

export interface RelatedQuote {
  id: string
  number: string
  status: string
  total_net: number
  total_gross: number
  currency: string
  valid_until: string | null
  owner: UserRef | null
  created_at: string
}

export interface RelatedInvoice {
  id: string
  number: string
  invoice_type: string
  status: string
  total_net: number
  total_gross: number
  amount_due: number
  due_date: string | null
  owner: UserRef | null
  created_at: string
}

export interface RelatedData {
  contacts?: { count: number; items: RelatedContact[] }
  companies?: { count: number; items: { id: string; name: string }[] }
  deals?: { count: number; items: RelatedDeal[] }
  tasks?: { count: number; items: RelatedTask[] }
  service_tickets?: { count: number; items: RelatedServiceTicket[] }
  documents?: { count: number; items: RelatedDocument[] }
  quotes?: { count: number; items: RelatedQuote[] }
  invoices?: { count: number; items: RelatedInvoice[] }
  notes?: { count: number }
}

export function useEntityRelated(
  entityType: string,
  entityId: string | undefined
) {
  const resource = RESOURCE_PATHS[entityType] ?? entityType
  return useQuery({
    queryKey: ["entity_related", entityType, entityId],
    queryFn: () =>
      api.get<{ data: RelatedData }>(
        `/api/v1/${resource}/${entityId}/related`
      ),
    enabled: !!entityType && !!entityId,
  })
}
